import React, { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { PinStep, LengthPicker } from "./LockScreen.jsx";
import { lastBackupAt } from "./backupReminder.js";
import { lockSettings, pinProblem, setPin, verifyPin, removeLock, setAutoLock, biometricAvailable, enrollBiometric, disableBiometric } from "./appLock.js";

const AUTO = [[0, "Whenever I leave the app"], [60, "After 1 minute"], [300, "After 5 minutes"], [900, "After 15 minutes"]];

export default function LockSettings({ signedIn = false, onBackup, onOpenAccount, onLockNow, onChanged }) {
  const [cfg, setCfg] = useState(lockSettings());
  const [step, setStep] = useState(null);     // null | "new" | "confirm" | "old" | "verify-off" | "change-new" | "change-confirm"
  const [tmp, setTmp] = useState("");
  const [len, setLen] = useState(6);          // length chosen for a new PIN
  const [bioOK, setBioOK] = useState(false);
  const [note, setNote] = useState("");
  const [ack, setAck] = useState(false);          // ticked on the "back up first" prompt
  const [, tick] = useState(0);                  // re-read the last-backup time after a backup
  useEffect(() => { biometricAvailable().then(setBioOK); }, []);

  const refresh = () => { setCfg(lockSettings()); setStep(null); setTmp(""); };
  // Setting a PIN with no account is the risky case: a forgotten PIN then means erasing the device. Ask for a backup first.
  const startNew = (next) => { setNote(""); setAck(false); setLen(cfg?.pinLength || 6); setStep(next === "new" && !signedIn ? "gate" : next); };
  const lastBackup = lastBackupAt();

  const checkCurrent = async (pin) => {
    const r = await verifyPin(pin);
    if (r.ok) return "";
    return r.wait ? `Too many wrong tries. Wait ${r.wait}s.` : `Wrong PIN. ${r.triesLeft} tries left.`;
  };
  const curLen = cfg?.pinLength || 6;

  return (
    <div className="bg-card rounded-2xl p-4 mb-3">
      <div className="flex items-center gap-2">
        <Lock size={16} className="text-brass" />
        <span className="text-sm font-semibold">App lock</span>
        <span className="text-[10px] px-2 py-0.5 rounded-full ml-auto" style={{ background: "var(--card2)", color: "var(--ivory-dim)" }}>{cfg ? `On · ${cfg.pinLength} digits` : "Off"}</span>
      </div>
      <p className="text-xs text-ivory-dim mt-1 leading-relaxed">Ask for a 4 or 6 digit PIN when the app opens. It keeps people who pick up your unlocked phone out. It isn't encryption of the data on the device, so keep your phone's own lock screen on too. If you forget the PIN you can reset it with a code emailed to your account.</p>

      {!step && !cfg && <button onClick={() => startNew("new")} className="mt-3 w-full bg-gold-grad text-ink font-semibold rounded-full py-2 text-sm">Set a PIN</button>}

      {!step && cfg && (
        <div className="mt-3 space-y-2.5">
          <label className="block text-xs text-ivory-dim">Lock the app
            <select value={cfg.autoLockSec} onChange={(e) => { setAutoLock(Number(e.target.value)); setCfg(lockSettings()); }} className="w-full rounded-lg px-2.5 py-2 text-sm mt-1" style={{ background: "var(--card2)", color: "var(--ivory)", border: "1px solid var(--hair)" }}>
              {AUTO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          {bioOK && (
            <label className="flex items-center justify-between text-sm">
              <span>Unlock with fingerprint or face</span>
              <input type="checkbox" checked={cfg.biometric} onChange={async (e) => {
                setNote("");
                try { if (e.target.checked) await enrollBiometric(); else disableBiometric(); setCfg(lockSettings()); }
                catch (err) { setNote("Couldn't set that up on this device. Your PIN still works."); }
              }} />
            </label>
          )}
          {note && <div className="text-xs" style={{ color: "var(--rust)" }}>{note}</div>}
          {!signedIn && (
            <div className="text-xs text-ivory-dim leading-relaxed">
              No account on this device. Last backup: {lastBackup ? new Date(lastBackup).toLocaleDateString() : "never"}.{" "}
              <button onClick={() => { onBackup?.(); tick((n) => n + 1); }} className="text-brass font-medium underline">Back up now</button>
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button onClick={onLockNow} className="flex-1 bg-card2 rounded-full py-2 text-sm font-medium">Lock now</button>
            <button onClick={() => startNew("old")} className="flex-1 bg-card2 rounded-full py-2 text-sm font-medium">Change PIN</button>
          </div>
          <button onClick={() => setStep("verify-off")} className="w-full text-xs py-1.5" style={{ color: "var(--rust)" }}>Turn off app lock</button>
        </div>
      )}

      {step === "gate" && (
        <div className="mt-4">
          <div className="text-sm font-semibold">Before you set a PIN, back up your data</div>
          <p className="text-xs text-ivory-dim mt-1.5 leading-relaxed">You don't have an account, so your data exists only on this device. If you forget the PIN, the only way back in is to erase this device's data and start over. A backup lets you restore everything afterwards.</p>
          <div className="mt-3 space-y-2">
            <button onClick={() => { onBackup?.(); tick((n) => n + 1); }} className="w-full bg-card2 rounded-full py-2 text-sm font-medium">Download a backup now</button>
            <button onClick={onOpenAccount} className="w-full bg-card2 rounded-full py-2 text-sm font-medium">Create a free account for automatic backup</button>
          </div>
          <p className="text-[11px] text-ivory-dim mt-2 leading-relaxed">An account keeps an online copy up to date automatically and lets you reset a forgotten PIN by email. Browsers can't save files on their own, so without an account we'll remind you to download a backup now and then.</p>
          <label className="flex items-start gap-2 text-xs mt-4 leading-relaxed">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" />
            <span>I've backed up my data, or I understand I could lose it if I forget my PIN.</span>
          </label>
          <button onClick={() => setStep("new")} disabled={!ack} className="mt-3 w-full bg-gold-grad text-ink font-semibold rounded-full py-2 text-sm disabled:opacity-40">Continue</button>
          <button onClick={refresh} className="w-full mt-1 py-2 text-xs text-ivory-dim">Cancel</button>
        </div>
      )}

      {(step === "new" || step === "change-new") && <LengthPicker value={len} onChange={(n) => { setLen(n); }} />}
      {step === "new" && <PinStep key={`new${len}`} title="Choose a PIN" hint={len === 4 ? "4 digits is quicker; 6 is harder to guess." : "Avoid birthdays and easy runs."} length={len} onCancel={refresh} onDone={(p) => { const e = pinProblem(p, len); if (e) return e; setTmp(p); setStep("confirm"); return ""; }} />}
      {step === "confirm" && <PinStep title="Type it again" length={len} onCancel={refresh} onDone={async (p) => { if (p !== tmp) { setStep("new"); return "Those didn't match. Start again."; } await setPin(p); refresh(); onChanged?.(true); return ""; }} />}
      {step === "old" && <PinStep title="Enter your current PIN" length={curLen} onCancel={refresh} onDone={async (p) => { const e = await checkCurrent(p); if (e) return e; setStep("change-new"); return ""; }} />}
      {step === "change-new" && <PinStep key={`cn${len}`} title="Choose a new PIN" length={len} onCancel={refresh} onDone={(p) => { const e = pinProblem(p, len); if (e) return e; setTmp(p); setStep("change-confirm"); return ""; }} />}
      {step === "change-confirm" && <PinStep title="Type the new PIN again" length={len} onCancel={refresh} onDone={async (p) => { if (p !== tmp) { setStep("change-new"); return "Those didn't match. Start again."; } await setPin(p); refresh(); onChanged?.(true); return ""; }} />}
      {step === "verify-off" && <PinStep title="Enter your PIN to turn the lock off" length={curLen} onCancel={refresh} onDone={async (p) => { const e = await checkCurrent(p); if (e) return e; removeLock(); refresh(); onChanged?.(false); return ""; }} />}
    </div>
  );
}
